import { createReactiveState, watch, type ReactiveStore } from '@/utils/reactive-store';

import { arraysEqual } from '../utility/miscFunctions';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { SpellingGroup } from './SpellCheckHandler';
import type { StyleCategory } from './StyleHandler';
import type { BrandFitCategory } from './WordListHandler/BrandFitHandler';

export interface TextUiStateNext {
  view: TeoView;
}

export interface TextUiState {
  view: TeoView;
  previousView: TeoView | undefined;
  markers: string[];
  dimension: string | undefined;
  textProperty: string | undefined;
  dimensionDrawerOpen: boolean;
  title: string;
  brandFitCategory: BrandFitCategory | undefined;
  brandFitWordList: string | undefined;
  dictionaryCategory: string | undefined;
  dictionaryWordList: string | undefined;
  styleCategory: StyleCategory | undefined;
  styleTextProperty: string | undefined;
  spellCheckCategory: string | undefined;
  spellCheckGroup: SpellingGroup | undefined;
}

export type TextUiEvents = {
  [K in keyof TextUiState]: (value: TextUiState[K]) => void;
} & {
  changed: (state: TextUiState) => void;
};

export type TeoView =
  | 'new'
  | 'brand-scanner'
  | 'dictionary'
  | 'text-generator'
  | 'performance'
  | 'plain-language'
  | 'simple-language'
  | 'spellcheck'
  | 'style'
  | 'versions';

export class TextUi {
  public readonly state: TextUiState;
  public readonly events = new TeoEventTarget<TextUiEvents>();
  public readonly rs: ReactiveStore<TextUiStateNext>;

  constructor(options: Partial<TextUiState> = {}) {
    this.state = {
      view: 'new',
      previousView: undefined,
      markers: [],
      dimension: '',
      textProperty: '',
      dimensionDrawerOpen: false,
      title: '',
      brandFitCategory: undefined,
      brandFitWordList: undefined,
      dictionaryCategory: undefined,
      dictionaryWordList: undefined,
      styleCategory: undefined,
      styleTextProperty: undefined,
      spellCheckCategory: undefined,
      spellCheckGroup: undefined,
      ...options,
    };
    // slowly migrate to reactive store in TextUi
    this.rs = createReactiveState({
      view: this.state.view,
    });
    this.events.addEventListener('view', (view: TeoView) => {
      this.rs.view = view;
    });
    watch(this.rs, (rs) => {
      if (rs.view !== this.state.view) {
        this.update('view', rs.view);
      }
    });
  }

  update<K extends keyof TextUiState>(
    property: K,
    value: TextUiState[K],
    skipCheck: boolean = false,
  ): void {
    if (skipCheck || this.propertyIsDifferent(property, value)) {
      if (property === 'view' && value !== this.state.view) {
        this.state.previousView = this.state.view;
      }
      this.state[property] = value;
      this.events.triggerEvent(property, value);
      this.events.triggerEvent('changed', this.state);
    }
  }

  private propertyIsDifferent<K extends keyof TextUiState>(
    property: K,
    value: TextUiState[K],
  ): boolean {
    return (
      (this.isArrayProperty(property) &&
        !arraysEqual(this.state[property] as string[], value as string[])) ||
      (!this.isArrayProperty(property) && this.state[property] !== value)
    );
  }

  private isArrayProperty<K extends keyof TextUiState>(property: K): boolean {
    return Array.isArray(this.state[property]);
  }
}
